'use client';

import React, { useState } from 'react';
import { X } from 'lucide-react';
import { EXPENSE_CATEGORIES, FARMS_LIST, PAYMENT_METHODS, ExpenseCategory } from './expensesData';

interface Props {
  onClose: () => void;
  onSave: (data: Record<string, unknown>) => void;
}

export default function ExpenseForm({ onClose, onSave }: Props) {
  const [farm, setFarm] = useState('');
  const [branch, setBranch] = useState('');
  const [category, setCategory] = useState<ExpenseCategory>('feed');
  const [description, setDescription] = useState('');
  const [vendor, setVendor] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [hasQty, setHasQty] = useState(false);
  const [quantity, setQuantity] = useState<number | ''>('');
  const [unit, setUnit] = useState('');
  const [unitCost, setUnitCost] = useState<number | ''>('');
  const [amount, setAmount] = useState<number | ''>('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [receiptRef, setReceiptRef] = useState('');
  const [notes, setNotes] = useState('');

  const selectedFarm = FARMS_LIST.find(f => f.name === farm);

  // Auto-calculate amount when qty & unit cost provided
  function handleQtyOrCostChange(field: 'qty' | 'cost', val: number) {
    if (field === 'qty') {
      setQuantity(val);
      if (unitCost !== '') setAmount(val * Number(unitCost));
    } else {
      setUnitCost(val);
      if (quantity !== '') setAmount(Number(quantity) * val);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave({ farm, branch, category, description, vendor, date, quantity, unit, unitCost, amount, paymentMethod, receiptRef, notes });
    onClose();
  }

  const selectedCat = EXPENSE_CATEGORIES.find(c => c.value === category);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Log Farm Expense</h2>
            <p className="text-xs text-gray-500 mt-0.5">Record a cost against a farm or branch</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Farm / Branch Scope */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Farm <span className="text-red-500">*</span></label>
              <select required value={farm} onChange={e => { setFarm(e.target.value); setBranch(''); }}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
                <option value="">Select farm</option>
                {FARMS_LIST.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Branch</label>
              <select value={branch} onChange={e => setBranch(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
                <option value="">All branches</option>
                {(selectedFarm?.branches ?? []).map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">Expense Category <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-3 gap-2">
              {EXPENSE_CATEGORIES.map(cat => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setCategory(cat.value)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold transition-all ${
                    category === cat.value
                      ? `${cat.bg} ${cat.color} border-current`
                      : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${category === cat.value ? 'bg-current' : 'bg-gray-300'}`} />
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description & Vendor */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Description <span className="text-red-500">*</span></label>
              <input required value={description} onChange={e => setDescription(e.target.value)}
                placeholder="e.g. Broiler Starter Mash (50 kg bags)"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Vendor / Supplier</label>
              <input value={vendor} onChange={e => setVendor(e.target.value)}
                placeholder="e.g. Ugachick Feeds Ltd"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
          </div>

          {/* Date & Payment */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Date <span className="text-red-500">*</span></label>
              <input required type="date" value={date} onChange={e => setDate(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Payment Method</label>
              <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
                {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Receipt / Ref #</label>
              <input value={receiptRef} onChange={e => setReceiptRef(e.target.value)}
                placeholder="Receipt number"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
          </div>

          {/* Quantity toggle */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer w-fit">
              <input type="checkbox" checked={hasQty} onChange={e => setHasQty(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500" />
              <span className="text-xs font-semibold text-gray-600">Enter quantity & unit cost (auto-calculates total)</span>
            </label>
          </div>

          {hasQty && (
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Quantity</label>
                <input type="number" min={0} value={quantity} onChange={e => handleQtyOrCostChange('qty', Number(e.target.value))}
                  placeholder="0"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Unit</label>
                <input value={unit} onChange={e => setUnit(e.target.value)}
                  placeholder="bags, litres, kg…"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Unit Cost (UGX)</label>
                <input type="number" min={0} value={unitCost} onChange={e => handleQtyOrCostChange('cost', Number(e.target.value))}
                  placeholder="0"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
            </div>
          )}

          {/* Total Amount */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Total Amount (UGX) <span className="text-red-500">*</span></label>
            <input required type="number" min={0} value={amount}
              onChange={e => setAmount(Number(e.target.value))}
              placeholder="0"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 font-semibold" />
            {amount !== '' && Number(amount) > 0 && (
              <p className="text-xs text-gray-400 mt-1">= UGX {Number(amount).toLocaleString()}</p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              placeholder="Additional details, justification, etc."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none" />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors">
            Cancel
          </button>
          <button type="submit" onClick={(e) => { e.preventDefault(); const form = document.querySelector('form'); form?.requestSubmit(); }}
            className="px-5 py-2 text-sm font-semibold bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-colors shadow-sm">
            Log Expense
          </button>
        </div>
      </div>
    </div>
  );
}
